









































































































































































import { Component, Vue } from 'vue-property-decorator';

import {curCoreClient, CoreClient} from '@/services/CoreClient';
import {Device, Channel, SelectOption, Info} from '@/types/core';
import { DateTime } from 'luxon';

import SectionHeader from '@/components/SectionHeader.vue';
import KeyValue from '@/components/KeyValue.vue';
import VueError from '@/components/VueError.vue';
import Success from '@/components/Success.vue'; 

import MessageSection from '@/components/device/MessageSection.vue';
import UpdateLocation from '@/components/device/UpdateLocation.vue';
import UpdateGroups from '@/components/device/UpdateGroups.vue';
import EnterpriseId from '@/components/EnterpriseId.vue';
import UpdateConfig from '@/components/device/UpdateConfig.vue';
import UnenrolDevice from '@/components/device/UnenrolDevice.vue';
import ReadingTable from '@/components/device/ReadingTable.vue';
import ReadingChart from '@/components/device/ReadingChart.vue';
import EditableInput from '@/components/EditableInput.vue';
import EditableSelect from '@/components/EditableSelect.vue';
import EventSection from '@/components/device/EventSection.vue';

import {FilterUtil} from '@/util/FilterUtil';
import DeviceMetaSection from "@/components/device/meta/DeviceMetaSection.vue";


@Component({
  components: {
    EditableInput,
    EditableSelect,
    SectionHeader,
    KeyValue,
    VueError,
    Success,
    UpdateLocation,
    UpdateGroups,
    UpdateConfig,
    EnterpriseId,
    MessageSection,
    UnenrolDevice,
    ReadingTable,
    ReadingChart,
    EventSection,
    DeviceMetaSection
  },
})
export default class DeviceView extends Vue {

  // our model
  dateTime = DateTime;

  device:Device = {} as Device;
  deviceName:string = '';
  deviceType:string = '';
  curChannel:Channel = {} as Channel;
  filterUtil:FilterUtil = new FilterUtil();
  
  mapCentre:any = {};

  errorMsg:string = '';
  deviceNameHttpError:string = '';
  deviceNameWaiting:boolean = false;
  editDeviceName = false;

  // device type
  deviceTypeList:SelectOption[] = [];
  deviceTypeHttpError:string = '';
  deviceTypeWaiting:boolean = false;
  editDeviceType = false;

  // lora parser
  loraParserWaiting = false;
  loraParserList:SelectOption[] = [];
  loraParserHttpError = '';
  editDeviceLoraParser = false;

  gmapOptions:object = {
    streetViewControl: false,
    mapTypeControl: false,
    zoom: 10
  };

  curSection:'info'|'update-location'|'update-groups'|'update-config'|'unenrol-device'|'device-list' = 'info';

  // internal dependencies
  private coreClient:CoreClient = curCoreClient;

  async mounted() {
    await this.loadInfo();
    await this.loadParsers();
    await this.loadDevice();
  }

  async loadInfo() {

    const reply = await this.coreClient.getInfo();

    if(reply.httpCode === 403)
      return this.$router.push({name:'login'});

    if(reply.errorMsg)
      return this.errorMsg = `Unable to retrieve user info: ${reply.errorMsg}`;

    this.deviceTypeList = reply.info!.deviceTypes.map( (deviceType:string) => ({label: this.filterUtil.formatDeviceType(deviceType), value: deviceType}));
  }

  async loadDevice() {

    const reply = await this.coreClient.getDevice({deviceId: this.$route.params.deviceId});

    if(reply.httpCode === 403)
      return this.$router.push({name:'login'});

    if(reply.httpCode === 404)
      return this.$router.push({name:'assets'});

    if(reply.errorMsg)
      return this.errorMsg = `Unable to retrieve device info: ${reply.errorMsg}`;

    this.device = reply.device!;
    this.deviceName = this.device.name || '';
    this.deviceType = this.device.deviceType || '';
  }

  async loadParsers() {

    // this code will break if we have more than 1k parsers (which is unlikely)
    const reply = await this.coreClient.getLoraParsers({skip:0, limit: 1000});

    if(reply.httpCode === 403)
      return this.$router.push({name:'login'});

    if(reply.errorMsg)
      return this.errorMsg = `Unable to retrieve lora parsers: ${reply.errorMsg}`;

    this.loraParserList = reply.loraParserList!.map(loraParser => ({label: `${loraParser.loraParserId} - ${this.filterUtil.safeTruncate(loraParser.name, 20)}`, value: loraParser.loraParserId}))
  }

  navbarClickHandler(section:'info'|'update-location'|'update-groups'|'update-config'|'unenrol-device'|'device-list') {

    if(section === 'device-list') {
      return this.$router.push({ name: 'assets'});
    }

    this.curSection = section;
    this.loadDevice();
  }
  
  
  get canConfigure():boolean {
    // return this.device.deviceType === 'aradGladiator';
    return true
  }

  get topIconList():any {

    let retList = [
      {name:'arrow-left', title:'back', event:'device-list', alert: false},
      {name:'sync-alt', title:'sync & refresh', event:'refresh'},
      {name:'map-marker-alt', title:'update location manually', event:'update-location'},
      {name:'layer-group', title:'update groups', event:'update-groups'},
    ];

    if(this.canConfigure)
      retList.push({name:'cogs', title:'config & downlinks', event:'update-config'});

    if(this.device.deviceType === 'nfresh')
      retList.push({name:'external-link-alt', title:'see in nfresh', event:'nfresh-link'});
    
    retList.push({name:'trash-alt', title:'unenrol device', event:'unerol-device', alert:true});

    return retList;
  }

  get loraParserLabel() {
    if(!this.device || !this.device.deviceId || !this.device.loraParserId)
      return '';

    const loraParser = this.loraParserList.find(lp => lp.value === this.device.loraParserId)
    
    return loraParser ? loraParser.label : '';
  }

  goToNfresh() {
    this.$router.push({ name: 'applications-nfresh-single-probe', params: { deviceId: this.device.deviceId }});
  }

  async updateName(newName:string) {
    this.deviceNameHttpError = '';
    this.deviceNameWaiting = true;
    const putReply = await this.coreClient.updateDeviceName({deviceId: this.device.deviceId, name: newName});

    this.deviceNameWaiting = false;

    if(putReply.httpCode === 403){
      return this.$router.push({name:'login'});
    }

    if(putReply.errorMsg){
      this.deviceNameHttpError = `Unable to update: ${putReply.errorMsg}`;
      this.editDeviceName = true;
      return;
    }

    this.deviceName = newName;

    // Show warning if TPW sync failed
    if(putReply.tpwSync?.attempted && !putReply.tpwSync?.success){
      this.deviceNameHttpError = `Name updated locally. Warning: TPW sync failed - ${putReply.tpwSync.error || 'changes may not be reflected in ThingPark'}`;
    }
  }

  async updateDeviceType(newType:string){

    // this allows for parser association removal
    const modNewType = newType !== '' ? newType : undefined;

    this.deviceTypeHttpError = '';
    this.deviceTypeWaiting = true;
    const putReply = await this.coreClient.updateDeviceType({deviceId: this.device.deviceId, deviceType: modNewType});

    this.deviceTypeWaiting = false;

    if(putReply.httpCode === 403){
      return this.$router.push({name:'login'});
    }

    if(putReply.errorMsg){
      this.deviceTypeHttpError = `Unable to update: ${putReply.errorMsg}`;
      this.editDeviceType = true;
      return;
    }

    this.deviceType = newType;

    // Show warning if TPW sync failed
    if(putReply.tpwSync?.attempted && !putReply.tpwSync?.success){
      this.deviceTypeHttpError = `Type updated locally. Warning: TPW sync failed - ${putReply.tpwSync.error || 'changes may not be reflected in ThingPark'}`;
    }
  }

  async updateDeviceLoraParser(loraParserId:string){
    
    // this allows for parser association removal
    const modLoraParserId = loraParserId !== '' ? loraParserId : undefined;

    this.loraParserHttpError = '';
    this.loraParserWaiting = true;
    const putReply = await this.coreClient.updateDeviceLoraParser({deviceId: this.device.deviceId, loraParserId: modLoraParserId});

    this.loraParserWaiting = false;

    if(putReply.httpCode === 403)
      return this.$router.push({name:'login'});
    
    if(putReply.errorMsg){
      this.loraParserHttpError = `Unable to update: ${putReply.errorMsg}`;
      this.editDeviceLoraParser = true;
      return;
    }

    // we update the local in memory copy
    this.device.loraParserId = loraParserId;
  }

  async refresh() {
    try {
      await this.coreClient.deviceTpwSync({deviceId: this.device.deviceId});
    } catch (err) {
      console.error('TPW sync failed during refresh:', err);
    }
    this.loadDevice();
  }
}
